import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useState } from "react";
import TOASTER from "../../../config/toaster";
import { COMMON_MSG } from "../../messages";
import { toast } from "react-toastify";
import { getFileNameWithDate } from "../../../helpers/file-export";
import {
  exportToExcel,
  exportToCsv,
  cleanDataForExport,
} from "../../../api/export-api-service";
import logger from "../../../helpers/logger";
import RoleButton from "../RoleButton";
import { BUTTON_TYPES } from "../../../config/permissions";

const ExportButton = ({ prefix, data, isDisabled, roleComponent, ...rest }) => {
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportExcel = async () => {
    if (!data.length) {
      toast.warn(COMMON_MSG.NoDataFound, { autoClose: TOASTER.AUTO_CLOSE });
      return;
    }

    handleExportClose();
    const toastId = toast.loading("Generating Excel file...");

    try {
      const cleanedData = cleanDataForExport(data);
      const fileName = getFileNameWithDate(prefix, "xlsx").replace(".xlsx", "");

      const result = await exportToExcel({
        data: cleanedData,
        fileName,
        sheetName: prefix,
      });

      if (result.success) {
        toast.update(toastId, {
          render: "Excel file downloaded successfully",
          type: "success",
          isLoading: false,
          autoClose: TOASTER.AUTO_CLOSE,
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      logger.error("Excel export error:", error);
      toast.update(toastId, {
        render: `Excel export failed: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: TOASTER.AUTO_CLOSE,
      });
    }
  };

  const handleExportCSV = async () => {
    if (!data.length) {
      toast.warn(COMMON_MSG.NoDataFound, { autoClose: TOASTER.AUTO_CLOSE });
      return;
    }

    handleExportClose();
    const toastId = toast.loading("Generating CSV file...");

    try {
      const cleanedData = cleanDataForExport(data);
      const fileName = getFileNameWithDate(prefix, "csv").replace(".csv", "");

      const result = await exportToCsv({
        data: cleanedData,
        fileName,
      });

      if (result.success) {
        toast.update(toastId, {
          render: "CSV file downloaded successfully",
          type: "success",
          isLoading: false,
          autoClose: TOASTER.AUTO_CLOSE,
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      logger.error("CSV export error:", error);
      toast.update(toastId, {
        render: `CSV export failed: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: TOASTER.AUTO_CLOSE,
      });
    }

    handleExportClose();
  };

  return (
    <> 
      <RoleButton
        roleButtonType={BUTTON_TYPES.EXPORT}
        roleComponent={roleComponent}
        tooltip={`You don't have permission to perform this action.`}
        roleIsIconBtn
        withTooltip="Download File"
        component="span"
        onClick={handleExportClick}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 60,
          width: 60,
          boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
          border: "1px solid rgba(255, 255, 255, 0.24)",
          borderRadius: 1,
          transition: "0.2s ease",
          color: "primary.main",
          "&:hover": {
            boxShadow: 5,
            color: "primary.light",
            backgroundColor: "action.hover",
          },
        }}
        disabled={isDisabled}
        {...rest}
      >   
        <FileDownloadIcon sx={{ fontSize: 35 }} />
      </RoleButton>

      {/* Download Options Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={handleExportCSV}>Export CSV</MenuItem>
        <MenuItem onClick={handleExportExcel}>Export Excel</MenuItem>
      </Menu>
    </>
  );
};

ExportButton.propTypes = {
  prefix: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
};

export default ExportButton;
